
import React from "react";
import { Schemes, Utils } from "../../../../utils";
import { i18nManager } from "../../../../i18n";
import * as ReactRouterDom from "react-router-dom";
import { Elements } from "../../../shared/Elements";
import { ConfirmDialog, Siren } from "../../../shared/ConfirmDialog";
import { Button, Checkbox, Form } from "@rivtn/noseur";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { ApiResponseData, BaseService, ConsusService, IHostWebsite, IMember, IProfileWebsite, ITeam, ITeamNews, ITeamWebsite, IUploadedFile, MemberService, SanitizedResponse, TeamService } from "../../../../services";
import { title } from "process";
import { ArticleDialog } from "../../../shared/settings/ArticleDialog";

export interface ActivitiesProps {
    team: ITeam;
    profile: IMember;
    teamWebsite: ITeamWebsite;
}

function Activities(props: ActivitiesProps) {

    const { team } = props;
    const teamService = TeamService.getInstance();
    const consusService = ConsusService.getInstance();
    const labels = i18nManager.Labels.teams.settings.news;
    const [news, setNews] = React.useState<ApiResponseData<ITeamNews>>({});
    const [teamWebsite, setTeamWebsite] = React.useState<ITeamWebsite>(props.teamWebsite);
    const [searchParams, setSearchParams] = ReactRouterDom.useSearchParams(window.location.search);

    React.useEffect(fetchInformation, []);

    return (<div className="profile-settings team-settings">
        <Checkbox scheme={Schemes.OMNEHOST} label={labels.enable_ad} defaultChecked={teamWebsite.news_settings.enable_rivtn_ad} onChecked={(checked) => {
            teamService.updateTeamWebsite(team.external_id, { ...teamWebsite, news_settings: { ...teamWebsite.news_settings, enable_rivtn_ad: checked } }).then(({ }: SanitizedResponse<any>) => {
                Siren.alert(labels.update_successful);
            }).catch(BaseService.reportError);
        }} />
        <hr />
        <Button scheme={Schemes.OMNEHOST} style={{ padding: "10px 20px" }} text={labels.publish_a_news} onClick={() => createOrEditNews()} />
        <hr />
        <div className="tnews">
            {!news.content
                ? (Elements.LOADING_STATE)
                : (news.content?.map((tnew) => {

                    return (<div className="tnew" key={tnew.external_id} onClick={() => createOrEditNews(tnew)}>
                        <span className="t">{tnew.title}</span>
                        <div className="c">
                            {(!!tnew.extra_data?.featured_image) && (<img alt={tnew.title} src={tnew.extra_data?.featured_image} />)}
                            <span className="lined-limit">{tnew.content}</span>
                        </div>
                    </div>);
                }))}
        </div>
        <div className="pagination">
            {Elements.buildPaginator(news ?? {},
                ({ currentPage }, size) => Utils.updateSearchParamses({ "page": currentPage, size }, setSearchParams, fetchInformation),
                parseInt(searchParams.get("page") ?? "1"), { margin: 15, marginBottom: 5 })}
            {Elements.buildSorter(labels.sorts, (sort) => Utils.updateSearchParamses({ sort }, setSearchParams, fetchInformation), searchParams.get("sort") ?? labels.sorts[0].label)}
        </div>
    </div>);

    function fetchInformation() {
        setNews({});
        teamService.queryTeamNews(props.team.external_id, {
            page: parseInt(searchParams.get("page") ?? "1"),
            size: parseInt(searchParams.get("size") ?? "10"),
        }).then(({ apiResponse }: SanitizedResponse<ITeamNews>) => {
            setNews(apiResponse.data!);
        }).catch(BaseService.reportError);
    }

    function createOrEditNews(news?: ITeamNews) {
        const dialogLabels = labels.dialog.create_edit;

        const dialog = ArticleDialog({
            article: news,
            labels: {
                update: dialogLabels.update,
                delete: dialogLabels.delete,
                publish: dialogLabels.publish,
                title: dialogLabels.create_team_news,
                titlePlaceholder: dialogLabels.title,
                delete_desc: labels.dialog.delete.desc,
                delete_title: labels.dialog.delete.title,
                updateTitle: dialogLabels.update_or_delete,
                contentPlaceholder: dialogLabels.content_in_markdown,
            },
            onPublishOrUpdate: (isCreate: boolean, title: string, content: string, selectedFile?: File | null, done?: (error?: string) => void) => {
                const featuredImageDeleted = selectedFile === null && news?.extra_data?.featured_image;
                const method = (!isCreate ? teamService.updateTeamNews : teamService.createTeamNews).bind(teamService);
                method(team.external_id, { title, content, external_id: news?.external_id, featured_image: (featuredImageDeleted ? null : news?.extra_data?.featured_image) }).then(({ sanitized: articleSanitized }) => {
                    Siren.alert(isCreate ? dialogLabels.publish_successful : dialogLabels.update_successful);
                    if (selectedFile) {
                        consusService.uploadFile("/team/news/featured/", `featured_${articleSanitized?.external_id}`, selectedFile).then(async ({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                            await teamService.updateTeamNews(team.external_id, { external_id: articleSanitized.external_id, featured_image: sanitized.consus_download_url });
                        }).catch(BaseService.reportError);
                    } else if (featuredImageDeleted) {
                        consusService.deleteFile([news?.extra_data?.featured_image]).catch(BaseService.reportError);
                    }
                    dialog.hide(() => {
                        done?.();
                        fetchInformation();
                    });
                }).catch((error: any) => {
                    done?.(error.errorMessage);
                    BaseService.reportError(error);
                });
            },
            onDelete: (article: { external_id?: string; }, done?: (error?: string) => void) => {
                teamService.deleteTeamNews(team.external_id, article!).then(() => {
                    Siren.alert(dialogLabels.delete_successful);
                    dialog.hide(() => {
                        done?.();
                        fetchInformation();
                    });
                }).catch((error: any) => {
                    done?.(error.errorMessage);
                    BaseService.reportError(error);
                });
            }
        });
        dialog.show();
    }

}

export default Activities;

